<script setup>
import { onMounted, reactive } from "vue";
import { updatePassword } from "@/services/userService";
import { customToast } from "@/globals/utils/customToast";
import { useUserStore } from "@/stores/user";
import DefaultButton from "@/components/utils/DefaultButton.vue";

const userStore = useUserStore();

// Form state
const form = reactive({
  name: "",
  email: "",
  phone: "",
});

const passwordForm = reactive({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});

// Error states
const errors = reactive({
  name: "",
  email: "",
  phone: "",
});

const passwordErrors = reactive({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});

// Save Information Handler
const saveInformation = async () => {
  // Reset errors
  Object.keys(errors).forEach((key) => (errors[key] = ""));

  // Validation logic...
  if (!Object.values(errors).some((error) => error)) {
    try {
      await userStore.update(form);
      customToast("Information updated successfully!", "success");
    }
    catch (error) {
      if (error.response?.status === 409) {
        errors.email = "Email is already in use.";
      } else {
        customToast("An error occurred. Please try again later.", "error");
      }
    }
  }
};

// Change Password Handler
const changePassword = async () => {
  // Validation logic...
  if (!Object.values(passwordErrors).some((error) => error)) {
    try {
      await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      customToast("Password updated successfully!", "success");
      passwordForm.confirmPassword = "";
      passwordForm.newPassword = "";
      passwordForm.currentPassword = "";
    } catch (error) {
      if (error.response?.status === 401) {
        passwordErrors.currentPassword = "Current password is incorrect.";
      } else {
        customToast("An error occurred. Please try again later.", "error");
      }
    }
  }
};

onMounted(async () => {
  let user = await userStore.info();
  form.name = user.name;
  form.email = user.email;
  form.phone = user.phone;
});
</script>

<template>
  <div class="min-h-screen bg-gray-100 dark:bg-gray-800 flex justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="container mx-auto max-w-3xl space-y-8">
      <!-- Edit Information Section -->
      <section class="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Edit Information</h2>
        <form @submit.prevent="saveInformation" class="space-y-6">
          <!-- Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Name</label>
            <input
                type="text"
                id="name"
                v-model="form.name"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Enter your name"
            />
            <span v-if="errors.name" class="text-red-500 text-sm">{{ errors.name }}</span>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Email</label>
            <input
                type="email"
                id="email"
                v-model="form.email"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Enter your email"
            />
            <span v-if="errors.email" class="text-red-500 text-sm">{{ errors.email }}</span>
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Phone</label>
            <input
                type="tel"
                id="phone"
                v-model="form.phone"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Enter your phone number"
            />
            <span v-if="errors.phone" class="text-red-500 text-sm">{{ errors.phone }}</span>
          </div>

          <div class="w-full flex justify-center">
            <DefaultButton title="Save" type="submit"/>
          </div>
        </form>
      </section>

      <!-- Change Password Section -->
      <section class="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Change Password</h2>
        <form @submit.prevent="changePassword" class="space-y-6">
          <!-- Current Password -->
          <div>
            <label for="current-password" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Current Password</label>
            <input
                type="password"
                id="current-password"
                v-model="passwordForm.currentPassword"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-red-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Enter your current password"
            />
            <span v-if="passwordErrors.currentPassword" class="text-red-500 text-sm">{{ passwordErrors.currentPassword }}</span>
          </div>

          <!-- New Password -->
          <div>
            <label for="new-password" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">New Password</label>
            <input
                type="password"
                id="new-password"
                v-model="passwordForm.newPassword"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-red-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Enter your new password"
            />
            <span v-if="passwordErrors.newPassword" class="text-red-500 text-sm">{{ passwordErrors.newPassword }}</span>
          </div>

          <!-- Confirm New Password -->
          <div>
            <label for="confirm-password" class="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Confirm New Password</label>
            <input
                type="password"
                id="confirm-password"
                v-model="passwordForm.confirmPassword"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:outline-none focus:ring focus:ring-red-500 dark:bg-gray-700 dark:text-gray-300"
                placeholder="Confirm your new password"
            />
            <span v-if="passwordErrors.confirmPassword" class="text-red-500 text-sm">{{ passwordErrors.confirmPassword }}</span>
          </div>

          <!-- Change Password Button -->
          <div class="w-full flex justify-center">
            <DefaultButton title="Change password" type="submit"/>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>
